<script setup lang="ts">
import { Check } from "lucide-vue-next";
import { NftSkill } from "fungi-types";
import { useI18n } from "vue-i18n";
import { computed } from "vue";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    style?: "chip" | "corner";
    entries: NftSkill["entries"];
  }>(),
  {
    style: "chip",
  }
);

const entries = computed(() => {
  if (!props.entries) return [];
  return props.entries.map((entry) => {
    let genderLetter = "";
    if (entry.gender === "men") genderLetter = "M";
    else if (entry.gender === "women") genderLetter = "W";

    return {
      ...entry,
      levelPretty: `${genderLetter}${entry.level}`,
    };
  });
});
</script>

<template>
  <div v-if="props.entries?.length === 0"></div>
  <Tooltip v-else>
    <div
      @click.stop
      v-if="props.style === 'chip'"
      class="rounded-md text-neutral-300 capitalize cursor-pointer text-xs h-full items-center flex"
    >
      <div
        class="flex flex-row items-center gap-1 bg-black rounded-md text-green py-0.5 w-fit px-1"
      >
        <Check :size="15" :strokeWidth="3" />
      </div>
    </div>
    <slot></slot>
    <template #content>
      <span class="text-gray-500">{{
        t("pages.marketplace.filter-panel.gameweek-participation")
      }}</span>
      <div v-for="entry in entries" class="items-center py-0.25 gap-2 mt-2 text-xs">
        <div class="font-semibold border-b border-black mb-1 flex justify-between">
          <span>Gameweek</span>
          <span>{{ entry.gameweekNumber }}</span>
        </div>
        <div class="flex gap-1 justify-between items-center">
          <span class="text-neutral-400">{{ t("_shared.entry") }}</span>
          <div class="w-full border-dotted border-neutral-400 border-t-2 h-0.5"></div>
          <div class="flex gap-1 whitespace-nowrap">
            {{ t(`entry.${entry.draw?.toLocaleLowerCase().replace(" ", "-")}`) }}
          </div>
        </div>
        <div class="flex gap-1 justify-between items-center">
          <span class="text-neutral-400">{{ t("_shared.rank") }}</span>
          <div class="w-full border-dotted border-neutral-400 border-t-2 h-0.5"></div>
          <div class="flex gap-1">
            {{ entry.rank ? `n°${entry.rank}` : "" }}
          </div>
        </div>
        <div class="flex gap-1 justify-between items-center">
          <span class="text-neutral-400">{{ t("_shared.court") }}</span>
          <div class="w-full border-dotted border-neutral-400 border-t-2 h-0.5"></div>
          <div class="flex gap-1 whitespace-nowrap">
            {{ entry.courtType }}
          </div>
        </div>
        <div class="flex gap-1 justify-between items-center">
          <span class="text-neutral-400">{{ t("_shared.level") }}</span>
          <div class="w-full border-dotted border-neutral-400 border-t-2 h-0.5"></div>
          <div class="flex gap-1">{{ entry.level }}</div>
        </div>
        <div class="flex gap-1 justify-between items-center">
          <span class="text-neutral-400">{{ t("_shared.country") }}</span>
          <div class="w-full border-dotted border-neutral-400 border-t-2 h-0.5"></div>
          <div class="flex gap-1">{{ entry.countryIso }}</div>
        </div>
        <div class="flex gap-1 justify-between items-center">
          <span class="text-neutral-400">{{ t("_shared.city") }}</span>
          <div class="w-full border-dotted border-neutral-400 border-t-2 h-0.5"></div>
          <div class="flex gap-1 whitespace-nowrap">{{ entry.city }}</div>
        </div>
      </div>
    </template>
  </Tooltip>
</template>
